import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../services/auth/auth.service';
import { Login2AuthService } from '../services/auth/login2-auth.service';
import { RbacService } from '../services/rbac/rbac.service';

@Component({
  selector: 'app-login2',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login2.component.html',
  styleUrl: './login2.component.scss',
})
export class Login2Component {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly login2AuthService = inject(Login2AuthService);
  private readonly rbacService = inject(RbacService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);

  readonly loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  isSubmitting = false;

  onLogin(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const email = String(this.loginForm.get('email')?.value ?? '').trim();
    const password = String(this.loginForm.get('password')?.value ?? '');

    this.isSubmitting = true;
    this.login2AuthService.login(email, password).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.rbacService.clearContext();
        this.rbacService.ensureContextLoaded().subscribe(() => {
          const requested = this.authService.consumeRedirectUrl();
          const target = requested ?? '/device-list';
          const destination = this.rbacService.canAccessRoute(target)
            ? target
            : this.rbacService.getDefaultRoute();
          this.router.navigateByUrl(destination, { replaceUrl: true });
        });
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting = false;
        const apiMessage = this.resolveErrorMessage(err);
        this.toastr.error(
          apiMessage || 'Invalid Email or Password',
          'Login Failed',
          { timeOut: 6000, closeButton: true, progressBar: true }
        );
      },
    });
  }

  onCancel(): void {
    this.loginForm.reset({
      email: '',
      password: '',
    });
    this.loginForm.markAsPristine();
    this.loginForm.markAsUntouched();
  }

  private resolveErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 0) {
      return 'Unable to reach the authentication service. Please check your network connection and try again.';
    }
    const body = err.error;
    const record =
      body && typeof body === 'object'
        ? (body as Record<string, unknown>)
        : null;
    const candidates = [
      typeof body === 'string' ? body : null,
      record?.['detail'],
      record?.['message'],
      record?.['error'],
    ];
    for (const candidate of candidates) {
      if (typeof candidate !== 'string') {
        continue;
      }
      const text = candidate.trim();
      // The service still sends this key in `message`. The sentence is in `detail`.
      if (text && !/^error\.http\.\d{3}$/i.test(text)) {
        return text;
      }
    }

    if (err.status === 401 || err.status === 403) {
      return '';
    }
    return 'An unexpected error occurred. Please try again.';
  }
}
